import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { 
  Calendar, 
  Clock, 
  User, 
  Eye, 
  ArrowLeft, 
  BookOpen,
  Tag,
  Facebook,
  Twitter,
  Linkedin,
  Link as LinkIcon
} from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { SEOHead } from '@/components/SEOHead';
import { BlogAdSense } from '@/components/blog/BlogAdSense';

interface BlogPost {
  id: string;
  titulo: string;
  slug: string;
  resumo: string;
  conteudo: string;
  imagemCapa: string | null;
  tempoLeitura: number;
  publicadoEm: string;
  visualizacoes: number;
  geradoPorIA: boolean;
  categoria: {
    id: string;
    nome: string;
    slug: string;
    cor: string;
  };
  autor: {
    id: string;
    nome: string;
  };
  tags: {
    id: string;
    nome: string;
    slug: string;
  }[];
  imagens: Array<{
    url: string;
    alt: string;
    autor: string;
    linkAutor: string;
  }>;
}

interface PostRelacionado {
  id: string;
  titulo: string;
  slug: string;
  resumo: string;
  imagemCapa: string | null;
  tempoLeitura: number;
  publicadoEm: string;
  categoria: {
    nome: string;
    slug: string;
  };
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relacionados, setRelacionados] = useState<PostRelacionado[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      carregarPost();
      window.scrollTo(0, 0);
    }
  }, [slug]);

  const carregarPost = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/blog/posts/${slug}`);
      setPost(data.post);
      setRelacionados(data.relacionados);
    } catch (error) {
      console.error('Erro ao carregar post:', error);
    } finally {
      setLoading(false);
    }
  };

  const compartilhar = (plataforma: string) => {
    const url = encodeURIComponent(window.location.href);
    const titulo = encodeURIComponent(post?.titulo || '');

    const links: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${titulo}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };

    if (links[plataforma]) {
      window.open(links[plataforma], '_blank', 'width=600,height=400');
    }
  };

  const copiarLink = () => {
    navigator.clipboard.writeText(window.location.href);
    // Toast seria ideal aqui
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <BookOpen className="w-16 h-16 text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold text-gray-700 mb-2">
          Artigo não encontrado
        </h1>
        <p className="text-gray-500 mb-6">
          O artigo que você procura pode ter sido removido ou não existe.
        </p>
        <Link to="/blog">
          <Button variant="nuvem">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para o Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <article className="min-h-screen bg-white">
      <SEOHead
        title={`${post.titulo} | Blog Fort3D`}
        description={post.resumo}
        keywords={post.tags.map(t => t.nome).join(', ')}
        url={`https://fort3d.com.br/blog/${post.slug}`}
        image={post.imagemCapa || undefined}
        type="article"
      />

      {/* Header */}
      <div className="bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar para o Blog</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Categoria */}
            <Link
              to={`/blog?categoria=${post.categoria.slug}`}
              className="inline-block px-4 py-1 rounded-full text-white text-sm font-medium mb-4"
              style={{ backgroundColor: post.categoria.cor }}
            >
              {post.categoria.nome}
            </Link>

            {/* Título */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {post.titulo}
            </h1>

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-gray-300">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.autor.nome}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatarData(post.publicadoEm)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.tempoLeitura} min de leitura
              </span>
              <span className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                {post.visualizacoes} visualizações
              </span>
              {post.geradoPorIA && (
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                  Gerado por IA
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Imagem de capa */}
      {post.imagemCapa && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl overflow-hidden shadow-2xl"
          >
            <img
              src={post.imagemCapa}
              alt={post.titulo}
              className="w-full h-64 md:h-96 object-cover"
            />
          </motion.div>
        </div>
      )}

      {/* Conteúdo */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar de compartilhamento */}
          <aside className="lg:w-16 flex-shrink-0">
            <div className="lg:sticky lg:top-24 flex lg:flex-col gap-3">
              <button
                onClick={() => compartilhar('facebook')}
                className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
                title="Compartilhar no Facebook"
              >
                <Facebook className="w-5 h-5" />
              </button>
              <button
                onClick={() => compartilhar('twitter')}
                className="w-10 h-10 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
                title="Compartilhar no Twitter"
              >
                <Twitter className="w-5 h-5" />
              </button>
              <button
                onClick={() => compartilhar('linkedin')}
                className="w-10 h-10 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
                title="Compartilhar no LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </button>
              <button
                onClick={copiarLink}
                className="w-10 h-10 rounded-full bg-gray-600 text-white flex items-center justify-center hover:opacity-80 transition-opacity"
                title="Copiar link"
              >
                <LinkIcon className="w-5 h-5" />
              </button>
            </div>
          </aside>

          {/* Artigo */}
          <div className="flex-1">
            {/* Resumo */}
            <p className="text-xl text-gray-600 italic border-l-4 border-[#FF6B35] pl-6 mb-8">
              {post.resumo}
            </p>

            {/* AdSense - In-article */}
            <BlogAdSense tipo="in-article" />

            {/* Conteúdo em Markdown */}
            <div className="prose prose-lg max-w-none prose-headings:text-[#1a1a2e] prose-a:text-[#FF6B35] hover:prose-a:text-[#e55a2b] prose-strong:text-[#1a1a2e]">
              <ReactMarkdown
                components={{
                  img: ({ src, alt }) => (
                    <figure className="my-8">
                      <img
                        src={src}
                        alt={alt}
                        className="w-full rounded-lg shadow-lg"
                      />
                      {alt && (
                        <figcaption className="text-center text-sm text-gray-500 mt-2">
                          {alt}
                        </figcaption>
                      )}
                    </figure>
                  ),
                }}
              >
                {post.conteudo}
              </ReactMarkdown>
            </div>

            {/* AdSense - In-article 2 */}
            <BlogAdSense tipo="in-article" />

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <Link
                      key={tag.id}
                      to={`/blog?tag=${tag.slug}`}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-[#FF6B35] hover:text-white transition-colors"
                    >
                      {tag.nome}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Compartilhamento no final */}
            <div className="mt-12 p-6 bg-gradient-to-r from-[#FF6B35]/10 to-[#00D9FF]/10 rounded-2xl">
              <h3 className="text-lg font-semibold mb-4">Compartilhe este artigo</h3>
              <div className="flex gap-3">
                <Button
                  onClick={() => compartilhar('facebook')}
                  className="bg-[#1877F2] hover:bg-[#1877F2]/90"
                >
                  <Facebook className="w-4 h-4 mr-2" />
                  Facebook
                </Button>
                <Button
                  onClick={() => compartilhar('twitter')}
                  className="bg-[#1DA1F2] hover:bg-[#1DA1F2]/90"
                >
                  <Twitter className="w-4 h-4 mr-2" />
                  Twitter
                </Button>
                <Button
                  onClick={copiarLink}
                  variant="outline"
                >
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Copiar Link
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Posts relacionados */}
      {relacionados.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#1a1a2e] mb-8">
              Artigos Relacionados
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relacionados.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/blog/${rel.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="h-48 overflow-hidden">
                    {rel.imagemCapa ? (
                      <img
                        src={rel.imagemCapa}
                        alt={rel.titulo}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#FF6B35] to-[#00D9FF]" />
                    )}
                  </div>
                  <div className="p-6">
                    <span className="text-sm text-[#FF6B35] font-medium">
                      {rel.categoria.nome}
                    </span>
                    <h3 className="text-lg font-bold text-[#1a1a2e] mt-2 mb-3 group-hover:text-[#FF6B35] transition-colors line-clamp-2">
                      {rel.titulo}
                    </h3>
                    <p className="text-gray-500 text-sm line-clamp-2">
                      {rel.resumo}
                    </p>
                    <div className="flex items-center gap-4 mt-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {rel.tempoLeitura} min
                      </span>
                      <span>{formatarData(rel.publicadoEm)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AdSense - Bottom */}
      <BlogAdSense tipo="bottom" />
    </article>
  );
}
